// Frame-bust: the landing page must not be embedded in another frame.
// The about page itself iframes /playground.html (same-origin); if THIS
// page is being framed, we would recursively self-embed (landing→landing
// →landing…). Navigating window.top to our own URL pops out of any
// surrounding frame BEFORE any React/StrictMode/render side-effect runs,
// which is why this lives at module top — `throw` alone would still allow
// the DOM and event listeners to be attached.
//
// Defence in depth: the server also sends `Content-Security-Policy:
// frame-ancestors 'self'` (see frontend/sample_nginx.conf), which blocks
// cross-origin framing at the browser level. This script-side bust catches
// the same-origin recursive case.
if (typeof window !== 'undefined' && window.self !== window.top) {
  try {
    window.top!.location.href = window.location.href;
  } catch {
    // Cross-origin top frame — CSP frame-ancestors should already have
    // refused to render us, but if we got here, bail out hard.
    document.body.innerHTML = '';
    throw new Error('landing page must not be iframed');
  }
}

import './styles/tailwind.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { LandingPage } from './modules/ui/components/LandingPage';
import { CookieGate } from './modules/ui/components/DisclaimerModal';
import { withBase } from './lib/withBase';

// Preload the hero-session JSON. Injected from JS (rather than a
// static <link> in landing.html) so the URL matches the eventual
// fetch in LandingPage.tsx under any deploy base — a literal href
// in HTML either misses the base prefix (absolute path) or gets
// asset-bundled by Vite into a hashed name the fetch can't find
// (relative path).
{
  const preload = document.createElement('link');
  preload.rel = 'preload';
  preload.as = 'fetch';
  preload.crossOrigin = 'anonymous';
  preload.href = withBase('/sessions/session-landingpage.json');
  document.head.appendChild(preload);
}

const root = createRoot(document.getElementById('landing-root')!);
root.render(
  <StrictMode>
    <CookieGate>
      <LandingPage />
    </CookieGate>
  </StrictMode>
);

// ---------------------------------------------------------------------------
// Debug bridge: window.playbackDebug()
// ---------------------------------------------------------------------------
//
// The landing hero canvas is driven by playbackFactory.ts. Once the session
// loads, playbackFactory sets `window.__playbackDebug` with live helpers.
// `playbackDebug()` is a convenience accessor so devtools users don't have
// to remember the underscore-prefixed name:
//
//   playbackDebug()?.getBufferSize()   // → number of dense frames buffered
//
// Returns null if the session hasn't loaded yet.
//
(window as Window & { playbackDebug?: () => Record<string, unknown> | null }).playbackDebug =
  (): Record<string, unknown> | null =>
    (window as any).__playbackDebug ?? null;

// ---------------------------------------------------------------------------
// Legacy: window.viewerWindow()
// ---------------------------------------------------------------------------
//
// The Playground section embeds the full viewer in an iframe. This helper
// returns that iframe's contentWindow for DevTools debugging.
//
(window as Window & { viewerWindow?: () => Window | null }).viewerWindow =
  (): Window | null => {
    // The playground iframe is the only one on the landing page (the hero
    // background is now a canvas, not an iframe).
    const iframe = document.querySelector('iframe');
    return iframe?.contentWindow ?? null;
  };
